"use client";
import styled from 'styled-components';
import BackButton from '../utils/back_button';
import {blog16} from "../../../assets/images";
import { useNavigate } from 'react-router-dom';
import OtherBlogs from '../our_blogs/OtherBlogs';
import CTASection from "../../landing/landing_home/empower"

export default function Blog15() {
 const navigate = useNavigate();
          const handleBackClick = () => {
             window.scrollTo(0, 0);
             navigate("/blog")
          };

  return (
    <>
      <Fullpage>
        <PageBackground>

          <BlogContainer>
          <MainContent>
              <HeaderSection>
                <BackButtonWrapper>
                  <BackButton onClick={handleBackClick} />
                </BackButtonWrapper>
                <BlogHeader
                  title="Smart Forms Fill by Campus Insights"
                  date="October 07 2025"
                  author="POSTED BY CAMPUS INSIGHTS"
                />
              </HeaderSection>

              <HeroImage
                src={blog16}
                alt="Teaching in the Age of Intelligence feature illustration"
              />

            <BlogContent />
          </MainContent>
          </BlogContainer>

        <OtherBlogs currentBlogId={15} />
        </PageBackground>
      </Fullpage>
      
      <CTASection />
    </>
  );
};

const BlogHeader = ({ title, date, author }) => {
  return (
    <HeaderContainer>
      <Title>{title}</Title>
      <MetadataContainer>
        <MetadataWrapper>
          <MetadataGroup>
            <DateText>{date}</DateText>
            <AuthorText>{author}</AuthorText>
          </MetadataGroup>
        </MetadataWrapper>
      </MetadataContainer>
    </HeaderContainer>
  );
};

const BlogContent = () => {
  return (
    <ContentContainer>

      <ContentSection title="Turn student silence into action">
        <ContentParagraph>
           AI-FormFill is Campus Insights’ newest feature that helps colleges close the loop on incomplete forms, missed deadlines, and stalled processes—automatically. Whether it’s financial aid, registration, or advising intake, AI-FormFill nudges students to finish what they started and fills in the gaps using smart, secure data.
        </ContentParagraph>
        <ContentParagraph>
          No more chasing down missing info. No more guessing why students disengage. Just faster completions, better outcomes, and fewer drop-offs.
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="Why It Matters">
        <FeatureList>
          <FeatureItem>40% of students abandon key forms due to confusion, fatigue, or lack of follow-up.</FeatureItem>
          <FeatureItem>Incomplete paperwork is a top contributor to melt, missed aid, and delayed graduation.</FeatureItem>
          <FeatureItem>Staff spend hours manually tracking and reminding students—AI-FormFill does it instantly.</FeatureItem>
        </FeatureList>
      </ContentSection>

      <ContentSection title="Top Benefits">
        <SubSection>
          <SubHeading>Boost Completion Rates</SubHeading>
          <BodyText>
             Automatically re-engage students who’ve started but not finished forms.
          </BodyText>
          <SubHeading>Reduce Staff Workload</SubHeading>
          <BodyText>
             Let AI handle the follow-up so your team can focus on high-touch support.
          </BodyText>
          <SubHeading>Improve Student Experience</SubHeading>
          <BodyText>
            Personalized nudges and autofill suggestions make forms faster and less frustrating.
          </BodyText>
          <SubHeading>Increase Retention</SubHeading>
          <BodyText>
            Fewer missed steps means fewer missed opportunities to stay enrolled.
          </BodyText>
          <SubHeading>Smart Nudging</SubHeading>
          <BodyText>
            Track bottlenecks, drop-off points, and completion trends in real time.
          </BodyText>
        </SubSection>
      </ContentSection>

      <ContentSection title="Core Features">
        <SubSection>
          <SubHeading>Smart Nudging</SubHeading>
          <BodyText>
             Detects incomplete forms and sends timely, personalized reminders via SMS, email, or chat.
          </BodyText>
          <SubHeading>Predictive Autofill</SubHeading>
          <BodyText>
             Uses secure student data to suggest or auto-complete missing fields—reducing friction.
          </BodyText>
          <SubHeading>Form Analytics Dashboard</SubHeading>
          <BodyText>
            Visualize completion rates, drop-off points, and engagement trends across departments.
          </BodyText>
          <SubHeading>Customizable Workflows</SubHeading>
          <BodyText>
            Tailor nudges and autofill logic to fit your institution’s forms, tone, and timing.
          </BodyText>
          <SubHeading>Secure & FERPA-Compliant</SubHeading>
          <BodyText>
            Built with student privacy and institutional trust at the core.
          </BodyText>
        </SubSection>
      </ContentSection>

      <ContentSection title="Key Takeaways">
        <FeatureList>
          <FeatureItem>AI-FormFill helps colleges recover lost completions and reduce melt—without adding staff burden.</FeatureItem>
          <FeatureItem>It’s proactive, personalized, and proven to improve form completion and student follow-through.</FeatureItem>
          <FeatureItem>Institutions can launch in weeks, not months, with minimal lift and maximum impact.</FeatureItem>
          <FeatureItem>It’s not just automation—it’s student success, accelerated.</FeatureItem>
        </FeatureList>
      </ContentSection>

      <ContentSection title="What Partners Are Saying">
        <QuoteSection>
          <QuoteText>
            “We saw a 28% increase in completed advising forms within the first month. Smart Forms didn’t just save time it helped us catch students before they slipped away.”
          </QuoteText>
          <QuoteAuthor>
            — Director of Student Success, Midwestern Community College
          </QuoteAuthor>
        </QuoteSection>
        <QuoteSection>
          <QuoteText>
            “Campus Insights helped us identify where students were dropping off and re-engage them automatically. It’s like having a digital safety net.”
          </QuoteText>
          <QuoteAuthor>
            — Director of Student Success, Midwestern Community College
          </QuoteAuthor>
        </QuoteSection>
        <QuoteSection>
          <QuoteText>
            “Our staff was drowning in follow-up emails. Now, Smart Forms handles the reminders, and our team can focus on real conversations.”
          </QuoteText>
          <QuoteAuthor>
            — Assistant Dean of Advising, Urban Tech College
          </QuoteAuthor>
        </QuoteSection>
      </ContentSection>

    </ContentContainer>
  );
};

const ContentSection = ({ title, children }) => {
  return (
    <SectionContainer>
      {title && <SectionTitle>{title}</SectionTitle>}
      <ContentWrapper>{children}</ContentWrapper>
    </SectionContainer>
  );
};

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const SectionTitle = styled.h2`
  color: #1c1b1f;
  letter-spacing: -0.32px;
  font: 700 32px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 28px;
  }

  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
`;

const IntroSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const IntroParagraph = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
  a {
    color: #5e2bff; /* Purple accent */
    text-decoration: underline;
    font-weight: 600;
  }

  @media (max-width: 991px) {
    font-size: 15px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const ContentParagraph = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 15px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const Title = styled.h1`
  width: 1021px;
  color: #1c1b1f;
  letter-spacing: -0.48px;
  font: 700 48px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    width: 100%;
    font-size: 36px;
    line-height: 120%;
  }

  @media (max-width: 640px) {
    font-size: 28px;
    line-height: 120%;
  }
`;

const MetadataContainer = styled.div`
  display: flex;
  width: 1008px;
  align-items: center;
  gap: 16px;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const MetadataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
`;

const MetadataGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const DateText = styled.time`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;

  @media (max-width: 991px) {
    font-size: 14px;
  }

  @media (max-width: 640px) {
    font-size: 12px;
    letter-spacing: 1px;
  }
`;

const AuthorText = styled.span`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 500 16px/120% Inter, sans-serif;

  @media (max-width: 991px) {
    font-size: 14px;
  }

  @media (max-width: 640px) {
    font-size: 12px;
    letter-spacing: 1px;
  }
`;


const BlogContainer = styled.article`
  display: flex;
  width: 1120px;
  padding: 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  border-radius: 24px;
  margin: 0 auto;
  box-sizing: border-box;
  background-color: #fff;

  @media (max-width: 991px) {
    width: 100%;
    max-width: 768px;
    padding: 40px 32px;
    gap: 48px;
  }

  @media (max-width: 640px) {
    width: 100%;
    max-width: 100%;
    padding: 24px 16px;
    gap: 32px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  align-self: stretch;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  width: 1008px;
  align-items: center;
  gap: 16px;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const HeroImage = styled.img`
  width: 1008px;
  height: 400px;
  border-radius: 16px;
  object-fit: cover;

  @media (max-width: 991px) {
    width: 100%;
    height: 300px;
  }

  @media (max-width: 640px) {
    height: 240px;
  }
`;

const PageBackground = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 500px, #F5F5F9 100px, #F5F5F9 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 200px 0;
    flex-direction: column;

  @media (max-width: 640px) {
    padding: 40px 0;
    background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 100px, #ffffff 100px, #ffffff 100%);
  }
`;

const Fullpage = styled.div`
  height: 100%;
  width: 100%;
//   background-color: #e20707ff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
 

  @media (max-width: 640px) {
    padding: 40px 0;
   
  }
`;

const ProcessDescription = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter,sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const SubSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const SubHeading = styled.h4`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.2px;
  font: 400 20px/140% Inter, sans-serif;
  margin: 0;
  @media (max-width: 991px) {
    font-size: 18px;
  }
  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const BodyText = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  margin: 0;
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 7px;
  width: 100%;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  list-style-type: none;
  position: relative;
  padding-left: 20px;

  &::before {
    content: '•';
    color: #64636a;
    position: absolute;
    left: 0;
    top: 0;
    font-weight: 700;
    line-height: 160%;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
    &::before {
      line-height: 150%;
    }
  }
`;

const QuoteSection = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: center;
  justify-content: center;
  margin: 0;
`;

const QuoteText = styled.p`
  color: #64636a;
  letter-spacing: -0.08px;
  margin: 0;
  font-weight: 500;
  font-size: 18px;
  font-style: italic;
  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;
const QuoteAuthor = styled.span`
  display: block;
  margin-top: 8px;
  font-size: 18px;
  font-weight: 400;
  font-style: normal;
  color: #757476;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  } 
  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;